import React, { useState, useEffect, useRef } from "react"
import type {
  AdminTest,
  CreateTestRequest,
  UpdateTestRequest,
} from "../../../app/services/meseeknaApiV2"

type SimulationEditModalProps = {
  test: AdminTest | null
  isCreating: boolean
  onClose: () => void
  onSave: (data: CreateTestRequest | UpdateTestRequest) => Promise<void>
  isLoading: boolean
}

const SimulationEditModal: React.FC<SimulationEditModalProps> = ({
  test,
  isCreating,
  onClose,
  onSave,
  isLoading,
}) => {
  const [formData, setFormData] = useState<CreateTestRequest>({
    shortName: "",
    title: "",
    contents: "",
    manualDescription: "",
    simType: "not_assigned",
    active: "Y",
    forDemo: "N",
    pdfURL: "",
    videoURL: "",
    imageData: "",
  })

  const [jsonFile, setJsonFile] = useState<File | null>(null)
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageInputMode, setImageInputMode] = useState<"file" | "svg">("file")
  const [svgInput, setSvgInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const jsonFileInputRef = useRef<HTMLInputElement>(null)
  const imageFileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (test && !isCreating) {
      setFormData({
        shortName: test.shortName || "",
        title: test.title || "",
        contents: test.contents || "",
        manualDescription: test.manualDescription || "",
        simType: test.simType || "not_assigned",
        active: test.active === "Y" ? "Y" : "N",
        forDemo: test.forDemo === "Y" ? "Y" : "N",
        pdfURL: test.pdfURL || "",
        videoURL: test.videoURL || "",
        imageData: test.imageData || "",
      })
      setImagePreview(test.imageData || null)
      // Detect if existing image is SVG
      if (test.imageData?.startsWith("<svg")) {
        setImageInputMode("svg")
        setSvgInput(test.imageData)
      }
    }
  }, [test, isCreating])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleJsonFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setJsonError(null)

    if (!file) {
      setJsonFile(null)
      return
    }

    if (!file.name.endsWith(".json")) {
      setJsonError("Please select a JSON file")
      setJsonFile(null)
      return
    }

    try {
      const text = await file.text()
      // Validate JSON
      const parsed = JSON.parse(text)

      // Check for required fields in simulation JSON
      if (!parsed.name && !parsed.id) {
        setJsonError("JSON must contain at least 'name' or 'id' field")
        setJsonFile(null)
        return
      }

      setJsonFile(file)
      setFormData((prev) => ({
        ...prev,
        contents: text,
        // Auto-populate title and shortName from JSON if empty
        title: prev.title || parsed.name || "",
        shortName: prev.shortName || (parsed.name ? parsed.name.substring(0, 24) : ""),
        manualDescription: prev.manualDescription || parsed.description || "",
      }))
    } catch {
      setJsonError("Invalid JSON file")
      setJsonFile(null)
    }
  }

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file")
      return
    }

    // Check file size (limit to 2MB for DB storage)
    if (file.size > 2 * 1024 * 1024) {
      setError("Image file must be less than 2MB")
      return
    }

    // Convert to data URI
    const reader = new FileReader()
    reader.onload = (event) => {
      const dataUri = event.target?.result as string
      setFormData((prev) => ({
        ...prev,
        imageData: dataUri,
      }))
      setImagePreview(dataUri)
      setError(null)
    }
    reader.onerror = () => {
      setError("Failed to read image file")
    }
    reader.readAsDataURL(file)
  }

  const handleSvgInputChange = (value: string) => {
    setSvgInput(value)
    // Validate SVG
    if (value.trim() && !value.trim().startsWith("<svg")) {
      setError("SVG code must start with <svg")
      return
    }
    setError(null)
    setFormData((prev) => ({
      ...prev,
      imageData: value.trim(),
    }))
    setImagePreview(value.trim() || null)
  }

  const clearImage = () => {
    setFormData((prev) => ({
      ...prev,
      imageData: "",
    }))
    setImagePreview(null)
    setSvgInput("")
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = ""
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.shortName.trim()) {
      setError("Short name is required")
      return
    }
    if (!formData.title.trim()) {
      setError("Title is required")
      return
    }
    if (isCreating && !formData.contents) {
      setError("Please upload a simulation JSON file")
      return
    }

    try {
      await onSave(formData)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save simulation")
    }
  }

  const renderImagePreview = () => {
    if (!imagePreview) return null

    if (imagePreview.startsWith("<svg")) {
      return (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Preview:</p>
          <div
            className="h-24 w-24 rounded-md border border-gray-200 overflow-hidden"
            dangerouslySetInnerHTML={{ __html: imagePreview }}
          />
        </div>
      )
    }

    return (
      <div className="mt-2">
        <p className="text-xs text-gray-500 mb-1">Preview:</p>
        <img
          src={imagePreview}
          alt="Preview"
          className="h-24 w-24 object-cover rounded-md border border-gray-200"
          onError={() => setImagePreview(null)}
        />
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium mb-4">
          {isCreating ? "Create Simulation" : "Edit Simulation"}
        </h3>

        <form onSubmit={handleSubmit}>
          {/* Error display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Short Name */}
            <div>
              <label
                htmlFor="shortName"
                className="block text-sm font-medium text-gray-700 mb-1">
                Short Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="shortName"
                name="shortName"
                value={formData.shortName}
                onChange={handleInputChange}
                maxLength={24}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., crisis-sim-01"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.shortName.length}/24 characters
              </p>
            </div>

            {/* Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                maxLength={128}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Simulation Title"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label
              htmlFor="manualDescription"
              className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="manualDescription"
              name="manualDescription"
              value={formData.manualDescription}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Simulation description (supports HTML)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Sim Type */}
            <div>
              <label
                htmlFor="simType"
                className="block text-sm font-medium text-gray-700 mb-1">
                Simulation Type
              </label>
              <select
                id="simType"
                name="simType"
                value={formData.simType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="not_assigned">Not Assigned</option>
                <option value="business">Business</option>
                <option value="geopolitical">Geopolitical</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {/* Active */}
            <div>
              <label
                htmlFor="active"
                className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="active"
                name="active"
                value={formData.active}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>

            {/* For Demo */}
            <div>
              <label
                htmlFor="forDemo"
                className="block text-sm font-medium text-gray-700 mb-1">
                Demo Mode
              </label>
              <select
                id="forDemo"
                name="forDemo"
                value={formData.forDemo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
          </div>

          {/* JSON Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Simulation JSON {isCreating && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="file"
                ref={jsonFileInputRef}
                accept=".json"
                onChange={handleJsonFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => jsonFileInputRef.current?.click()}
                className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                {jsonFile ? "Change JSON File" : "Upload JSON File"}
              </button>
              {jsonFile && (
                <span className="text-sm text-green-600">{jsonFile.name}</span>
              )}
              {formData.contents && !jsonFile && !isCreating && (
                <span className="text-sm text-gray-500">
                  (Existing JSON data loaded)
                </span>
              )}
            </div>
            {jsonError && (
              <p className="text-sm text-red-600 mt-1">{jsonError}</p>
            )}
          </div>

          {/* Image Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Simulation Image
            </label>

            {/* Toggle between file upload and SVG input */}
            <div className="flex space-x-2 mb-2">
              <button
                type="button"
                onClick={() => setImageInputMode("file")}
                className={`px-3 py-1 text-xs rounded-md cursor-pointer ${
                  imageInputMode === "file"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                Upload Image
              </button>
              <button
                type="button"
                onClick={() => setImageInputMode("svg")}
                className={`px-3 py-1 text-xs rounded-md cursor-pointer ${
                  imageInputMode === "svg"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                Paste SVG Code
              </button>
            </div>

            {imageInputMode === "file" ? (
              <div className="flex items-center space-x-3">
                <input
                  type="file"
                  ref={imageFileInputRef}
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => imageFileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                  {formData.imageData && !formData.imageData.startsWith("<svg")
                    ? "Change Image"
                    : "Choose Image"}
                </button>
                {formData.imageData && (
                  <button
                    type="button"
                    onClick={clearImage}
                    className="text-sm text-red-600 hover:text-red-800 cursor-pointer">
                    Remove
                  </button>
                )}
                <span className="text-xs text-gray-500">Max 2MB</span>
              </div>
            ) : (
              <div>
                <textarea
                  value={svgInput}
                  onChange={(e) => handleSvgInputChange(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">...</svg>'
                />
                {svgInput && (
                  <button
                    type="button"
                    onClick={clearImage}
                    className="mt-1 text-sm text-red-600 hover:text-red-800 cursor-pointer">
                    Clear SVG
                  </button>
                )}
              </div>
            )}

            {renderImagePreview()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* PDF URL */}
            <div>
              <label
                htmlFor="pdfURL"
                className="block text-sm font-medium text-gray-700 mb-1">
                PDF URL
              </label>
              <input
                type="text"
                id="pdfURL"
                name="pdfURL"
                value={formData.pdfURL}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.com/guide.pdf"
              />
            </div>

            {/* Video URL */}
            <div>
              <label
                htmlFor="videoURL"
                className="block text-sm font-medium text-gray-700 mb-1">
                Video URL
              </label>
              <input
                type="text"
                id="videoURL"
                name="videoURL"
                value={formData.videoURL}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.com/video.mp4"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer transition-colors disabled:opacity-50">
              {isLoading ? "Saving..." : isCreating ? "Create" : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SimulationEditModal
